import { Box } from "@mui/material";
import { useState } from "react";
import V2Dialog from "../ui/V2Dialog";
import { PrimaryButton, TextButton } from "../ui/primitives";
import { v2 } from "../ui/v2Tokens";
import MealCard from "../builder/MealCard";
import { saveSavedMeal } from "../../../../services/NutritionV2Service";
import { createMeal, type MealV2, type SavedMealV2 } from "../model/nutritionV2Model";

type Props = {
  open: boolean;
  onClose: () => void;
  /** absent when creating a new one */
  meal?: SavedMealV2 | null;
  /** called after the server has stored it */
  onSaved: () => void;
};

/**
 * Create or edit a company saved meal.
 *
 * The editor is {@link MealCard} — the very same card the menu builder uses — so a saved meal is
 * built with the food search, the quantities, the substitution groups and the alternatives a trainer
 * already knows. Reusing it is the point: a saved meal IS a meal, and giving it a second, thinner
 * editor would make it look like a different kind of thing and would drift from the real one.
 *
 * The card's menu-only actions (reorder, duplicate, "save as a saved meal") are left off, because a
 * standalone meal has nothing to reorder against, nothing to duplicate into, and offering to save it
 * as itself is nonsense.
 *
 * The library list keeps its own inline rename. That path sends the NAME only, which the server
 * reads as "leave the items alone" — so renaming from the list can never empty a meal, and this
 * dialog is the only place its contents change.
 */
const SavedMealDialog = ({ open, onClose, meal, onSaved }: Props) => {
  const asMeal = (): MealV2 =>
    meal
      ? createMeal(0, {
          name: meal.name,
          timeFrom: meal.timeFrom || "",
          timeTo: meal.timeTo || "",
          items: meal.items,
        })
      : createMeal(0, { name: "ארוחה שמורה חדשה", items: [] });

  // Keyed remount by the meal being edited, so opening a different row re-seeds the card rather
  // than showing the previous one's contents.
  const [draft, setDraft] = useState<MealV2>(asMeal);
  const [seededFor, setSeededFor] = useState<string | undefined>(meal?.id);
  if (seededFor !== meal?.id) {
    setSeededFor(meal?.id);
    setDraft(asMeal());
  }

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEmpty = draft.items.length === 0;

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await saveSavedMeal({
        id: meal?.id || "",
        name: draft.name.trim() || "ארוחה שמורה",
        timeFrom: draft.timeFrom || undefined,
        timeTo: draft.timeTo || undefined,
        items: draft.items,
      });
      onSaved();
      onClose();
    } catch (e) {
      // Stays on screen: the meal exists only in this dialog, and closing it would lose the work.
      setError("השמירה נכשלה. הארוחה לא נשמרה — נסה שוב.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <V2Dialog
      open={open}
      onClose={onClose}
      title={meal ? "עריכת ארוחה שמורה" : "ארוחה שמורה חדשה"}
      subtitle="זמינה לכל המאמנים בחברה, בכל תפריט"
      maxWidth="760px"
      actions={
        <>
          <TextButton onClick={onClose}>ביטול</TextButton>
          <PrimaryButton onClick={() => void save()} disabled={isEmpty || saving}>
            {saving ? "שומר…" : "שמור ארוחה"}
          </PrimaryButton>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <MealCard meal={draft} onChange={setDraft} onDelete={onClose} />

        {isEmpty && (
          <Box sx={{ fontSize: "12.5px", color: v2.ink3 }}>
            הוסף לפחות מוצר אחד כדי לשמור את הארוחה.
          </Box>
        )}

        {error && (
          <Box
            data-testid="saved-meal-error"
            role="alert"
            sx={{
              padding: "10px 14px",
              borderRadius: v2.rSm,
              background: v2.overWash,
              border: `1px solid ${v2.over}`,
              color: v2.over,
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </V2Dialog>
  );
};

export default SavedMealDialog;
